<script setup lang="ts">
  import { computed, unref } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    ColoredButton,
    DefaultTable,
    TableHeaderSection,
    SearchBar,
    SearchToolbar,
    SelectInput,
    NoDocuments,
    SearchNoResults,
    PaginateOrScroll,
  } from '@/shared/ui';
  import { useTableFilterModal } from '@/shared/lib';
  import type { TrainingListFilters, TrainingListItem } from '@/entities/Training';
  import { usePermission } from '@/entities/Permission';
  import {
    useTrainingListSearch,
    TRAINING_LIST_QUANTITY_OPTIONS,
    TRAINING_LIST_ORDER_OPTIONS,
  } from '../model/useTrainingListSearch';
  import { useTrainingListSelection } from '../model/useTrainingListSelection';
  import { useTrainingListDelete } from '../model/useTrainingListDelete';
  import { useTrainingListExport } from '../model/useTrainingListExport';
  import TrainingListFilterModal from './TrainingListFilterModal.vue';
  import TrainingListTableRow from './TrainingListTableRow.vue';

  import AddIcon from '@/shared/assets/icons/AddIcon.svg';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';

  const TABLE_HEADERS = [
    'Título treinamento',
    'Rev.',
    'Data',
    'Colaboradores',
    'Tipo de treinamento',
    'Status',
    'Ações',
  ];

  const router = useRouter();
  const { can } = usePermission();
  const canCreateTraining = computed(
    () => can('create', 'Training').value && can('readMatrix', 'Training').value
  );

  const {
    items,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    isInitialLoading,
    error,
    limit,
    term,
    sort,
    order,
    filters,
    showControls,
    changePage,
    changeLimit,
    changeSearch,
    changeSort,
    applyFilters,
    clearFilters,
    fetch: reloadTrainings,
  } = useTrainingListSearch();
  const selection = useTrainingListSelection();

  const { isFilterActive, openFilterModal } = useTableFilterModal<TrainingListFilters>(
    TrainingListFilterModal,
    { clearFilters, filters: {} },
    applyFilters,
    () => filters.value
  );

  const { requestSingleDelete, requestBulkDelete } = useTrainingListDelete({
    selectedIds: () => selection.selectedIdList.value,
    removeFromSelection: (ids) => selection.remove(ids),
    reloadTrainings: () => {
      void reloadTrainings();
    },
  });

  const { handleExportToExcel, isExporting, hasExportableItems } = useTrainingListExport({
    metadata,
    term,
    sort,
    order,
    filters,
  });

  function goToCreateTraining(): void {
    router.push({ name: 'TreinamentosCriar' });
  }

  function goToViewTraining(id: number): void {
    router.push({ name: 'TreinamentosVisualizar', params: { id } });
  }

  function goToEditTraining(id: number): void {
    router.push({ name: 'TreinamentosEditar', params: { id } });
  }

  function goToTrainingControl(id: number): void {
    router.push({ name: 'TreinamentosControleTreinamento', params: { id } });
  }

  const isEmpty = computed(() => unref(items).length === 0 && !unref(loading));
  const isTrueEmptyState = computed(
    () => !unref(showControls) && !unref(loading) && !unref(error)
  );
</script>

<template>
  <div data-testid="training-list-tab-page" class="flex flex-col gap-4">
    <div class="flex gap-3">
      <ColoredButton
        v-if="canCreateTraining"
        data-testid="btn-criar-treinamento"
        text="Criar Treinamento"
        :icon="AddIcon"
        icon-alt="Criar treinamento"
        @click="goToCreateTraining"
      />
      <ColoredButton
        data-testid="btn-exportar-excel"
        text="Exportar para Excel"
        :icon="ExportExcelIcon"
        icon-alt="Exportar para Excel"
        :disabled="!hasExportableItems || isExporting"
        :loading="isExporting"
        @click="handleExportToExcel"
      />
    </div>

    <SearchToolbar
      v-if="!isTrueEmptyState"
      class="mb-2"
    >
      <template #search>
        <SearchBar
          :initial-search-term="term"
          class="w-full"
          @search="changeSearch"
        />
      </template>
      <template #quantity>
        <SelectInput
          :model-value="limit"
          :options="TRAINING_LIST_QUANTITY_OPTIONS"
          arrow-class="w-5 h-5"
          shadow-class="shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
          @update:model-value="changeLimit"
        />
      </template>
    </SearchToolbar>

    <div
      v-if="error"
      data-testid="training-error-state"
      class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
    >
      {{ error }}
    </div>

    <div
      v-else-if="isInitialLoading"
      data-testid="training-loading-state"
      class="flex items-center justify-center py-12"
    >
      <span class="text-sm text-gray-05">Carregando treinamentos...</span>
    </div>

    <template v-else-if="showControls || loading">
      <PaginateOrScroll
        data-testid="training-paginate"
        :items="items"
        :all-items="items"
        :metadata="metadata"
        :selected-quantity="limit"
        :loading-more="loadingMore"
        :has-more-items="hasMoreItems"
        entity-name="treinamento"
        @page-changed="changePage"
      >
        <template #table="{ items: pageItems }">
          <DefaultTable>
            <TableHeaderSection
              title="Lista de Treinamentos"
              :headers="TABLE_HEADERS"
              :has-selected="selection.hasSelected.value"
              :all-selected="selection.isAllSelected(pageItems.map((i: TrainingListItem) => i.id))"
              :partially-selected="
                selection.isPartiallySelected(pageItems.map((i: TrainingListItem) => i.id))
              "
              :selected-count="selection.selectedCount.value"
              :selected-documents="selection.selectedIdList.value"
              :item-count="pageItems.length"
              item-type="treinamentos"
              :order-options="TRAINING_LIST_ORDER_OPTIONS"
              :current-sort="sort"
              :current-order="order"
              :has-filter="true"
              :is-filter-active="isFilterActive"
              :has-delete="true"
              :delete-disabled="!selection.hasSelected.value"
              :show-column-headers="!isEmpty"
              th-class="text-left text-xs font-semibold text-gray-07 px-4 py-3 w-min whitespace-nowrap align-middle"
              @update:sort="changeSort"
              @toggle-all="selection.toggleAll(pageItems.map((i: TrainingListItem) => i.id))"
              @filter-clicked="openFilterModal"
              @delete="requestBulkDelete"
            />
            <tbody class="border-t border-gray-02">
              <tr v-if="isEmpty">
                <td :colspan="TABLE_HEADERS.length + 1" class="py-8">
                  <SearchNoResults data-testid="training-no-results-state" />
                </td>
              </tr>
              <TrainingListTableRow
                v-for="item in pageItems"
                :key="item.id"
                :training="item"
                :selected="selection.isSelected(item.id)"
                @toggle="selection.toggle(item.id)"
                @view="goToViewTraining(item.id)"
                @edit="goToEditTraining(item.id)"
                @delete="requestSingleDelete(item.id)"
                @control="goToTrainingControl(item.id)"
              />
            </tbody>
          </DefaultTable>
        </template>
      </PaginateOrScroll>
    </template>

    <NoDocuments
      v-else
      data-testid="training-empty-state"
      title="Ops! Parece que ainda não foram criados treinamentos"
    />
  </div>
</template>
